import type { ChatClient } from '@wtfalch/chat';
import { useEffect, useRef, useState } from 'react';

import type { ChatTimelinePage } from './types.ts';

/** How close to an edge counts as "there," for auto-scroll and pagination --
 *  a plain, generous pixel band rather than the exact edge, since either
 *  test firing a frame late (a font swap, a reaction row growing) should
 *  still count. */
const NEAR = 80;

/**
 * A room's (or a thread's) timeline: the currently-known page, kept live
 * off `client.onTimelineChanged`, with the scrolling behaviour a stream
 * needs -- stuck to the bottom while the reader is there, holding position
 * while older history loads in above, and marking the room read once the
 * newest message is actually on screen. Own rewrite of the same shape
 * `@wtfalch/chat`'s own (unused here) React package built for this, since
 * Agora draws its own screens against `ChatClient` alone (see the chat
 * section's own docblock).
 */
export function useTimeline(
  client: ChatClient,
  roomId: string,
  threadId: string | null,
): {
  page: ChatTimelinePage;
  readUpToId: string | null;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
} {
  const [page, setPage] = useState<ChatTimelinePage>(() => client.timeline(roomId, threadId));
  const [readUpToId] = useState<string | null>(() =>
    threadId ? null : client.readUpToEventId(roomId),
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);
  const pagination = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const lastRead = useRef<string | null>(null);

  useEffect(() => {
    setPage(client.timeline(roomId, threadId));
    stickToBottom.current = true;
    lastRead.current = null;
    return client.onTimelineChanged(roomId, () => setPage(client.timeline(roomId, threadId)));
  }, [client, roomId, threadId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || page.events.length === 0) return;
    const snapshot = pagination.current;
    if (snapshot) {
      el.scrollTop = el.scrollHeight - snapshot.scrollHeight + snapshot.scrollTop;
      pagination.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [page]);

  // Reading the visible end of the room's own timeline sends its receipt.
  // Never inside a thread: a thread has no read marker of its own (ticket
  // #17), and merely mounting it must not mark the room's messages read.
  useEffect(() => {
    if (threadId) return;
    const newest = page.events.filter((e) => e.status === 'sent').at(-1)?.id;
    if (!newest || lastRead.current === newest) return;
    lastRead.current = newest;
    client.markRoomRead(roomId).catch(() => {
      if (lastRead.current === newest) lastRead.current = null;
    });
  }, [client, roomId, threadId, page]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR;
    if (pagination.current || !page.hasMore) return;
    if (el.scrollTop > NEAR) return;
    pagination.current = { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight };
    client.paginate(roomId, threadId).catch(() => {
      pagination.current = null;
    });
  }

  return { page, readUpToId, scrollRef, onScroll };
}
